import { useState, useEffect, useCallback } from "react"
import { demographicFields } from "~/data/demographics"

const STORAGE_KEY = "core-snapshot-demographics"

function loadFromStorage(): Record<string, string> | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Record<string, string>
  } catch {
    return null
  }
}

function saveToStorage(data: Record<string, string>): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // fail silently
  }
}

export function clearDemographicsStorage(): void {
  if (typeof window === "undefined") return
  localStorage.removeItem(STORAGE_KEY)
}

export function useDemographics() {
  const [values, setValues] = useState<Record<string, string>>({})
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const saved = loadFromStorage()
    if (saved) {
      setValues(saved)
    }
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (hydrated) {
      saveToStorage(values)
    }
  }, [values, hydrated])

  const setValue = useCallback((fieldId: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }))
  }, [])

  const isComplete = demographicFields
    .filter((f) => f.required)
    .every((f) => {
      const val = values[f.id]?.trim()
      if (!val) return false
      if (f.type === "multi-select") {
        return val.split(",").filter(Boolean).length > 0
      }
      return true
    })

  return { values, setValue, isComplete, hydrated }
}
